import React from 'react';
import { useOffline } from '../context/OfflineContext';
import { Wifi, WifiOff, RefreshCw, Cloud, CloudOff } from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from './ui/tooltip';

export function OfflineIndicator() {
  const { isOnline, isSyncing, pendingCount, syncData, lastSyncTime } = useOffline();

  const formatLastSync = () => {
    if (!lastSyncTime) return 'Nie';
    const date = new Date(lastSyncTime);
    return date.toLocaleTimeString('de-CH', { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <TooltipProvider>
      <div className="flex items-center gap-2">
        {/* Pending sync indicator */}
        {pendingCount > 0 && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Badge variant="secondary" className="gap-1 cursor-default">
                <Cloud className="h-3 w-3" />
                {pendingCount}
              </Badge>
            </TooltipTrigger>
            <TooltipContent>
              <p>{pendingCount} Änderung(en) warten auf Synchronisierung</p>
            </TooltipContent>
          </Tooltip>
        )}

        {/* Sync button */}
        {isOnline && pendingCount > 0 && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                onClick={syncData}
                disabled={isSyncing}
              >
                <RefreshCw className={`h-4 w-4 ${isSyncing ? 'animate-spin' : ''}`} />
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              <p>Jetzt synchronisieren</p>
            </TooltipContent>
          </Tooltip>
        )}

        {/* Online/Offline status */}
        <Tooltip>
          <TooltipTrigger asChild>
            <div className={`flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium ${
              isOnline 
                ? 'bg-green-500/10 text-green-500' 
                : 'bg-amber-500/10 text-amber-500'
            }`}>
              {isOnline ? (
                <>
                  <Wifi className="h-3 w-3" />
                  <span className="hidden sm:inline">Online</span>
                </>
              ) : (
                <>
                  <WifiOff className="h-3 w-3" />
                  <span className="hidden sm:inline">Offline</span>
                </>
              )}
            </div>
          </TooltipTrigger>
          <TooltipContent>
            {isOnline ? (
              <p>Verbunden • Letzte Sync: {formatLastSync()}</p>
            ) : (
              <p>Offline-Modus aktiv • Änderungen werden lokal gespeichert</p>
            )}
          </TooltipContent>
        </Tooltip>
      </div>
    </TooltipProvider>
  );
}

// Offline banner for prominent display
export function OfflineBanner() {
  const { isOnline, pendingCount, isSyncing, syncData } = useOffline();

  if (isOnline && pendingCount === 0) return null;

  return (
    <div className={`px-4 py-2 text-sm flex items-center justify-between ${
      isOnline 
        ? 'bg-blue-500/10 text-blue-500 border-b border-blue-500/20' 
        : 'bg-amber-500/10 text-amber-500 border-b border-amber-500/20'
    }`}>
      <div className="flex items-center gap-2">
        {isOnline ? (
          <>
            <Cloud className="h-4 w-4" />
            <span>{pendingCount} Änderung(en) werden synchronisiert...</span>
          </>
        ) : (
          <>
            <CloudOff className="h-4 w-4" />
            <span>Offline-Modus • {pendingCount > 0 ? `${pendingCount} Änderung(en) warten` : 'Alle Daten verfügbar'}</span>
          </>
        )}
      </div>
      
      {isOnline && pendingCount > 0 && (
        <Button
          variant="ghost"
          size="sm"
          className="h-7 text-blue-500 hover:text-blue-600"
          onClick={syncData}
          disabled={isSyncing}
        >
          {isSyncing ? (
            <>
              <RefreshCw className="h-3 w-3 mr-1 animate-spin" />
              Synchronisiere...
            </>
          ) : (
            <>
              <RefreshCw className="h-3 w-3 mr-1" />
              Jetzt sync
            </>
          )}
        </Button>
      )}
    </div>
  );
}
